import { Box, Flex, Image } from '@chakra-ui/react';
import Link from 'next/link';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components';
import WebLinks from '~/components/WebLinks';
import { ALL_SOCIAL_PLATFORM_TYPE } from '~/constants';
import { getPlatformProfileLink } from '~/core/utils';
import { mapCreatorPlatformsFromApiToVm } from '~/creatorUtils.mapper';
import { useGetCreatorLocation } from '~/hooks/useGetCreatorLocation';
import { getConnectedPlatforms } from '~/screens/ManagePlatforms';
import { ICreator } from '~/types';
import { formatAbbr } from '~/utils';

import { CategoryTagCard } from './CategoryTagCard';

interface Props {
  creator: ICreator;
  mediaKitId: string;
}

const CreatorPlatforms = ({ creator }: { creator: Props['creator'] }) => {
  const platforms = getConnectedPlatforms(
    // @ts-ignore - until I figure this out
    mapCreatorPlatformsFromApiToVm(creator.platforms),
    creator.isOffPlatform,
  );

  return (
    <div className="flex justify-center gap-4">
      {platforms.map((platform) => (
        <Link
          key={platform.platform}
          href={
            getPlatformProfileLink(
              platform.platform as ALL_SOCIAL_PLATFORM_TYPE,
              platform.handle as string,
            ) as string
          }
          target="_blank"
          className="flex flex-col items-center gap-2"
        >
          <div>
            <ContentCardPlatformIcon
              boxSize={6}
              platform={platform.platform as ALL_SOCIAL_PLATFORM_TYPE}
              color={platform.platform === 'snapchat' ? '#000' : undefined} // stroke color necessary for snap
            />
          </div>
          <span className="text-16 text-[var(--media-kit-text-subtle)]">
            {platform.count ? formatAbbr(platform.count) : '-'}
          </span>
        </Link>
      ))}
    </div>
  );
};

export const CreatorProfileCardBuilder: React.FC<Props> = ({
  creator,
  mediaKitId,
}) => {
  const { getLocation } = useGetCreatorLocation();

  return (
    <Flex
      flexDirection={{ base: 'column', md: 'row' }}
      justifyContent="center"
      alignItems="center"
      flex="1 0 0"
      alignSelf="stretch"
    >
      <Box
        width="100%"
        maxWidth={{
          md: '700px',
        }}
        maxHeight={{
          md: '700px',
        }}
        aspectRatio="1/1"
        borderRadius={{ base: 'none', md: '3xl' }}
        overflow="hidden"
        style={{
          viewTransitionName: `avatar-${mediaKitId}`,
        }}
      >
        <Image
          src={creator.avatar || 'randomstringtothrowerror'}
          draggable={false}
          width="100%"
          height="100%"
          objectFit="cover"
          aspectRatio="1"
          borderRadius={{ base: 'none', md: '3xl' }}
          fallbackStrategy="onError"
          fallback={
            <Box
              display="flex"
              width="100%"
              height="100%"
              justifyContent="center"
              alignItems="center"
              backgroundColor="neutral.50"
              border="1px solid"
              borderColor="primary.50"
              borderRadius={{ base: 'none', md: '3xl' }}
            >
              <svg
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17.8008 18.0938C17.8008 15.3841 19.9974 13.1875 22.707 13.1875C25.4167 13.1875 27.6133 15.3841 27.6133 18.0938C27.6133 20.8034 25.4167 23 22.707 23C19.9974 23 17.8008 20.8034 17.8008 18.0938ZM22.707 9.1875C17.7882 9.1875 13.8008 13.175 13.8008 18.0938C13.8008 23.0125 17.7882 27 22.707 27C27.6258 27 31.6133 23.0125 31.6133 18.0938C31.6133 13.175 27.6258 9.1875 22.707 9.1875ZM41.6465 19.709C41.6465 17.8914 43.1199 16.418 44.9375 16.418C46.7551 16.418 48.2285 17.8914 48.2285 19.709C48.2285 21.5266 46.7551 23 44.9375 23C43.1199 23 41.6465 21.5266 41.6465 19.709ZM44.9375 12.418C40.9108 12.418 37.6465 15.6823 37.6465 19.709C37.6465 23.7357 40.9108 27 44.9375 27C48.9642 27 52.2285 23.7357 52.2285 19.709C52.2285 15.6823 48.9642 12.418 44.9375 12.418ZM22.7074 35C15.7463 35 10.3438 41.0078 10.3438 48.2969V50.0618C12.8816 50.322 17.8026 50.75 22.7074 50.75C27.6122 50.75 32.5331 50.322 35.071 50.0618V48.2969C35.071 41.0078 29.6684 35 22.7074 35ZM38.855 45.4938C40.6444 45.624 42.7938 45.7344 44.9376 45.7344C48.3006 45.7344 51.6774 45.4627 53.6563 45.2691V44.4113C53.6563 39.2096 49.8112 34.9961 44.9376 34.9961C41.5013 34.9961 38.5763 37.0908 37.1499 40.1497C37.9809 41.8063 38.5629 43.6103 38.855 45.4938ZM34.6761 36.4667C31.7596 33.1424 27.5784 31 22.7074 31C13.2162 31 6.34375 39.1339 6.34375 48.2969V51.5869C6.34375 52.7072 7.16663 53.7283 8.36902 53.8697C9.73651 54.0305 16.2087 54.75 22.7074 54.75C29.206 54.75 35.6783 54.0305 37.0457 53.8697C38.2482 53.7283 39.071 52.7072 39.071 51.5869V49.5189C40.8327 49.6387 42.8828 49.7344 44.9376 49.7344C49.8174 49.7344 54.6708 49.1946 55.6968 49.074C56.8609 48.9371 57.6563 47.9482 57.6563 46.8664V44.4113C57.6563 37.3357 52.3413 30.9961 44.9376 30.9961C40.5689 30.9961 36.9275 33.2034 34.6761 36.4667Z"
                  fill="#3A495F"
                />
              </svg>
            </Box>
          }
        />
      </Box>
      <Flex
        className="border-1 border-[var(--media-kit-background-alpha)] bg-[var(--media-kit-background-primary)]"
        transform={{
          base: 'translateY(-48px) translateX(0)',
          md: 'translateX(-64px) translateY(0)',
        }}
        borderRadius="3xl"
        boxShadow={{
          base: 'none',
          md: '0px 4px 16px 0px rgba(28, 33, 40, 0.20)',
        }}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
        maxWidth={{ base: 'none', lg: '600px' }}
        gap={6}
        style={{
          // Removing viewTransitionName could cause the `avatar` to superimpose on this div.
          viewTransitionName: `card-outline-${mediaKitId}`,
        }}
      >
        <Flex
          flexDirection="column"
          gap={6}
          paddingTop={8}
          alignItems="center"
          paddingX={4}
        >
          <h2 className="text-center text-32 font-500 text-[var(--media-kit-text-base)]">
            {creator.fullName}
          </h2>
          <CreatorPlatforms creator={creator} />
          <WebLinks links={creator.webLinks} />
          <CategoryTagCard creator={creator} />
        </Flex>
        {creator.location || creator.age || creator.bioAgency ? (
          <div className="h-[1px] w-full bg-[var(--media-kit-background-alpha)]" />
        ) : null}

        <div className="flex w-full flex-col gap-6 text-[var(--media-kit-text-base)]">
          {creator.location || creator.age ? (
            <Flex justifyContent="space-between" paddingX={6}>
              {creator.location ? (
                <Flex gap={1} alignItems="center">
                  <span className="text-14 font-500">
                    {getLocation(creator)}
                  </span>
                </Flex>
              ) : (
                <Box>&nbsp;</Box>
              )}
              {creator.age && (
                <Flex gap={2}>
                  <p className="text-14 font-500">Age</p>
                  <p className="text-14 font-500 text-[var(--media-kit-text-subtle)]">
                    {creator.age}
                  </p>
                </Flex>
              )}
            </Flex>
          ) : null}
          <p className="px-6 pb-6 text-16">{creator.bioAgency}</p>
        </div>
      </Flex>
    </Flex>
  );
};
